import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PublicLayout, PageContainer, PageHeader } from "@/components/PublicLayout";
import { useSeo } from "@/lib/useSeo";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { MagnifyingGlass } from "@phosphor-icons/react";

const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020];

export default function ResultHistory() {
  useSeo({ title: "Guwahati Morning Teer Result History 2020-2026 | All Old Results", description: "Complete Guwahati Morning Teer result history from 2020 to 2026. Search all First Round and Second Round results by date and year.", path: "/result-history" });
  const [search, setSearch] = useState("");
  const [year, setYear] = useState(2026);
  const q = useQuery({
    queryKey: ["results", year],
    queryFn: async () => (await api.get(`/results?year=${year}`)).data,
  });

  const rows = (q.data || []).filter((r) => !search || r.date.includes(search) || (r.fr || "").includes(search) || (r.sr || "").includes(search));

  return (
    <PublicLayout>
      <PageContainer className="py-10">
        <PageHeader eyebrow="Full Archive" title="Result History" subtitle="Complete history of all published Morning Teer results from 2020 to 2026." />

        <div className="flex flex-wrap gap-2 mb-5" data-testid="history-year-selector">
          {YEARS.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              data-testid={`history-year-btn-${y}`}
              className={`px-4 h-11 rounded-xl font-bold text-sm transition-colors ${
                year === y ? "bg-brand text-white shadow-md" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {y}
            </button>
          ))}
        </div>

        <div className="relative mb-6 max-w-md">
          <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search date or number in this year…"
            data-testid="history-search"
            className="w-full h-12 pl-11 pr-4 rounded-xl border border-slate-200 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand/40"
          />
        </div>

        {q.isLoading ? (
          <LoadingState />
        ) : q.isError ? (
          <ErrorState onRetry={q.refetch} />
        ) : rows.length === 0 ? (
          <EmptyState title="No results found" subtitle="Try a different year or search." />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="history-grid">
            {rows.map((r, i) => (
              <div key={r.id || i} className="rounded-2xl bg-white border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] p-4" data-testid={`history-card-${i}`}>
                <p className="text-sm font-semibold text-slate-500">{r.date}</p>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="text-center bg-brand-light rounded-xl py-3">
                    <p className="text-[11px] font-bold text-brand uppercase tracking-wide">F/R</p>
                    <p className="font-heading font-black text-2xl text-brand tabular-nums">{r.fr || "-"}</p>
                  </div>
                  <div className="text-center bg-slate-50 rounded-xl py-3">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">S/R</p>
                    <p className="font-heading font-black text-2xl text-slate-800 tabular-nums">{r.sr || "-"}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </PageContainer>
    </PublicLayout>
  );
}
